import React, { useEffect, useState } from "react";
import { Alert, Spinner, Table } from "react-bootstrap";
import { FaCrown, FaUserMinus, FaUserPlus, FaUsersCog } from "react-icons/fa";

import api from "../api/axios";
import AdminShell from "../components/AdminShell";
import { useAuth } from "../context/AuthContext";

const initials = (name = "") =>
  name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "U";

function AdminUsers() {
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get("/auth/users")
      .then(({ data }) => active && setUsers(data))
      .catch(
        (err) =>
          active &&
          setError(err.response?.data?.message || "Failed to load users.")
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const changeRole = async (id, role) => {
    setError("");
    setBusyId(id);
    try {
      const { data } = await api.patch(`/auth/users/${id}/role`, { role });
      setUsers((prev) => prev.map((u) => (u.id === id ? data : u)));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update role.");
    } finally {
      setBusyId(null);
    }
  };

  const adminCount = users.filter((u) => u.role === "admin").length;

  return (
    <AdminShell
      icon={<FaUsersCog />}
      title="Manage Users"
      subtitle={
        loading
          ? "Loading…"
          : `${users.length} account${users.length === 1 ? "" : "s"} · ${adminCount} admin${
              adminCount === 1 ? "" : "s"
            }`
      }
    >
      <style>{`
        .usr-table { margin: 0; }
        .usr-table thead th {
          background: #fff8f5; color: #c2410c;
          font-size: .74rem; letter-spacing: .09em; text-transform: uppercase;
          font-weight: 700; border-bottom: 1px solid #f0e4dd; padding: 14px 16px;
          white-space: nowrap;
        }
        .usr-table tbody td {
          padding: 14px 16px; vertical-align: middle;
          border-bottom: 1px solid #f6eee9; color: #334155;
        }
        .usr-table tbody tr:last-child td { border-bottom: none; }
        .usr-table tbody tr:hover td { background: #fffaf7; }
        .usr-who { display: flex; align-items: center; gap: 12px; }
        .usr-av {
          width: 38px; height: 38px; border-radius: 11px; flex-shrink: 0;
          display: grid; place-items: center;
          font-size: .78rem; font-weight: 800; color: #0f172a;
          background: linear-gradient(135deg, #ff6b35, #ff9066);
        }
        .usr-name { font-weight: 700; color: #0f172a; }
        .usr-you {
          margin-left: 7px; font-size: .68rem; font-weight: 700;
          background: #ffe9e0; color: #c2410c;
          padding: 2px 8px; border-radius: 999px;
        }
        .usr-email { color: #64748b; font-size: .9rem; }
        /* Peach/slate rather than Bootstrap's green "success" pill */
        .usr-role {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 13px; border-radius: 999px;
          font-size: .74rem; font-weight: 700; letter-spacing: .03em;
        }
        .usr-role--admin { background: linear-gradient(135deg,#ff6b35,#ff9066); color: #0f172a; }
        .usr-role--user { background: #eef2f7; color: #475569; }
        .usr-act {
          display: inline-flex; align-items: center; gap: 7px;
          border-radius: 10px; font-weight: 700; font-size: .8rem;
          padding: 8px 14px; border: 1px solid transparent;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
          white-space: nowrap;
        }
        .usr-act--grant { background: linear-gradient(135deg,#ff6b35,#ff9066); color: #0f172a; }
        .usr-act--grant:hover:not(:disabled) { filter: brightness(1.08); color: #fff; }
        .usr-act--revoke { background: #fff; color: #b91c1c; border-color: #fecaca; }
        .usr-act--revoke:hover:not(:disabled) { background: #fef2f2; color: #b91c1c; }
        .usr-act:disabled { opacity: .6; cursor: not-allowed; }
        .usr-empty { padding: 48px 20px; text-align: center; color: #94a3b8; }

        /* Below md the table scrolls sideways and hides the action column —
           the one thing this page exists for. Stack into cards instead. */
        .usr-cards { display: none; }
        @media (max-width: 767.98px) {
          .usr-scroll { display: none; }
          .usr-cards { display: block; padding: 14px; }
        }
        .usr-card {
          border: 1px solid #f0e4dd; border-radius: 16px;
          padding: 16px; margin-bottom: 12px; background: #fffdfc;
        }
        .usr-card:last-child { margin-bottom: 0; }
        .usr-card__top {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; margin-bottom: 10px;
        }
        .usr-card__mail {
          color: #64748b; font-size: .86rem; word-break: break-all; margin-bottom: 12px;
        }
        .usr-card .usr-act { width: 100%; justify-content: center; }
        .usr-card__self {
          text-align: center; color: #94a3b8; font-size: .82rem;
          padding: 8px; background: #f8fafc; border-radius: 10px;
        }

        @media (prefers-reduced-motion: reduce) { .usr-act { transition: none; } }
      `}</style>

      {/* aria-live so a failed role change is announced, not only shown */}
      <div aria-live="polite">
        {error && <Alert variant="danger">{error}</Alert>}
      </div>

      <div className="adm-panel">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: "#ff6b35" }} />
          </div>
        ) : users.length === 0 ? (
          <div className="usr-empty">No accounts yet.</div>
        ) : (
          <>
          <div className="table-responsive usr-scroll">
            <Table hover className="usr-table align-middle">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = currentUser?.id === u.id;
                  const isAdmin = u.role === "admin";
                  return (
                    <tr key={u.id}>
                      <td>
                        <span className="usr-who">
                          <span className="usr-av">{initials(u.name)}</span>
                          <span className="usr-name">
                            {u.name}
                            {isSelf && <span className="usr-you">YOU</span>}
                          </span>
                        </span>
                      </td>
                      <td className="usr-email">{u.email}</td>
                      <td>
                        <span
                          className={`usr-role usr-role--${isAdmin ? "admin" : "user"}`}
                        >
                          {isAdmin && <FaCrown />}
                          {u.role}
                        </span>
                      </td>
                      <td className="text-end">
                        {/* You can't demote yourself out of admin access */}
                        {isSelf ? (
                          <span className="text-muted small">—</span>
                        ) : (
                          <button
                            type="button"
                            className={`usr-act usr-act--${isAdmin ? "revoke" : "grant"}`}
                            disabled={busyId === u.id}
                            onClick={() =>
                              changeRole(u.id, isAdmin ? "user" : "admin")
                            }
                          >
                            {busyId === u.id ? (
                              "…"
                            ) : (
                              <>
                                {isAdmin ? <FaUserMinus /> : <FaUserPlus />}
                                {isAdmin ? "Revoke admin" : "Make admin"}
                              </>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Same data, stacked — shown only below md */}
          <div className="usr-cards">
            {users.map((u) => {
              const isSelf = currentUser?.id === u.id;
              const isAdmin = u.role === "admin";
              return (
                <div className="usr-card" key={u.id}>
                  <div className="usr-card__top">
                    <span className="usr-who">
                      <span className="usr-av">{initials(u.name)}</span>
                      <span className="usr-name">
                        {u.name}
                        {isSelf && <span className="usr-you">YOU</span>}
                      </span>
                    </span>

                    <span className={`usr-role usr-role--${isAdmin ? "admin" : "user"}`}>
                      {isAdmin && <FaCrown />}
                      {u.role}
                    </span>
                  </div>

                  <div className="usr-card__mail">{u.email}</div>

                  {isSelf ? (
                    <div className="usr-card__self">
                      You can't change your own role
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={`usr-act usr-act--${isAdmin ? "revoke" : "grant"}`}
                      disabled={busyId === u.id}
                      onClick={() => changeRole(u.id, isAdmin ? "user" : "admin")}
                    >
                      {busyId === u.id ? (
                        "…"
                      ) : (
                        <>
                          {isAdmin ? <FaUserMinus /> : <FaUserPlus />}
                          {isAdmin ? "Revoke admin" : "Make admin"}
                        </>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}

export default AdminUsers;
